/**
 * N3: pure pieces of the "a new deploy reaches users without a hard
 * refresh" flow. Kept free of DOM, React and workbox imports so the page
 * (pwa-register.ts) and the worker (service-worker.ts) can both use them
 * and vitest can cover them directly.
 */

// ── Update polling ──────────────────────────────────────────────────────────

export interface UpdateCheckInput {
  /** document.hidden */
  hidden: boolean
  /** navigator.onLine */
  online: boolean
  now: number
  lastCheckAt: number
  minGapMs: number
}

/**
 * Whether to ask the browser to re-fetch the SW script now. Only a visible,
 * online tab polls (a background tab burns no requests), and checks closer
 * together than `minGapMs` are dropped so focus + visibilitychange + the
 * interval firing together cost one request.
 */
export function shouldCheckForUpdate(input: UpdateCheckInput): boolean {
  if (input.hidden || !input.online) return false
  return input.now - input.lastCheckAt >= input.minGapMs
}

// ── Reload loop guard ───────────────────────────────────────────────────────

export const RELOAD_GUARD_KEY = 'sw-update-reloaded-at'

export interface SlotStorage {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
}

/**
 * Claim the right to reload for a new service worker. Returns false when a
 * reload already happened within `windowMs` (tracked in sessionStorage so it
 * survives the reload itself): if the new worker somehow keeps reporting
 * "controlling" or "waiting" after every load, the tab reloads once and then
 * stops instead of looping. Storage being blocked still allows the reload;
 * the window check is a safety net, not a gate.
 */
export function claimReloadSlot(
  storage: SlotStorage | null,
  now: number,
  windowMs: number,
): boolean {
  if (!storage) return true
  let last = 0
  try {
    const raw = Number(storage.getItem(RELOAD_GUARD_KEY) ?? '0')
    last = Number.isFinite(raw) ? raw : 0
  } catch {
    return true
  }
  if (last > 0 && now - last < windowMs) return false
  try {
    storage.setItem(RELOAD_GUARD_KEY, String(now))
  } catch {
    /* storage blocked: reload anyway */
  }
  return true
}

// ── Navigation strategy (used inside the service worker) ───────────────────

export interface NavigationDeps {
  fetch: (request: Request) => Promise<Response>
  /** The precached app shell, or undefined when none is cached. */
  fallback: () => Promise<Response | undefined>
  /**
   * When a shell is cached, give the network this long before serving the
   * shell. LAN up but internet down hangs TCP for ~75 s; without this the
   * PWA showed a white page that whole time. Ignored when nothing is cached.
   */
  timeoutMs?: number
}

/**
 * Network-first for page navigations. The shell (index.html) names the
 * hashed JS/CSS of one deploy, so serving it from the precache meant the
 * first load after a deploy ran the PREVIOUS build until a hard refresh.
 * A fast network answer always wins; the precached shell is used when the
 * network fails, stalls past `timeoutMs`, or the edge returns a 5xx
 * mid-deploy, which keeps offline mode working. After a timeout the fetch
 * keeps running and its outcome is ignored.
 */
export async function networkFirstNavigation(
  request: Request,
  deps: NavigationDeps,
): Promise<Response> {
  const network = deps.fetch(request)
  const cached = await deps.fallback()
  if (!cached) return network
  // Late rejection after the timeout won the race must not go unhandled.
  network.catch(() => undefined)
  const fromNetwork = network.then(
    (res) => (res.status >= 500 ? cached : res),
    () => cached,
  )
  if (!deps.timeoutMs) return fromNetwork
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<Response>((resolve) => {
    timer = setTimeout(() => resolve(cached), deps.timeoutMs)
  })
  try {
    return await Promise.race([fromNetwork, timeout])
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Options for workbox `precacheAndRoute`. The precache route must never
 * answer a page navigation: "/" -> /index.html (`directoryIndex`) and
 * "/x" -> /x.html (`cleanURLs`) are both disabled, so every navigation goes
 * through `networkFirstNavigation` and an online load always gets the
 * current deploy's shell. The shell stays precached for the offline
 * fallback.
 */
export const PRECACHE_ROUTE_OPTIONS = { directoryIndex: '', cleanURLs: false } as const

/** Message the page posts to ask the waiting worker to activate only if no
 *  window of the app is visible. */
export const SKIP_WAITING_IF_ALL_HIDDEN = 'SKIP_WAITING_IF_ALL_HIDDEN'

/**
 * Whether a waiting worker may take over without anyone pressing Reload.
 * skipWaiting activates it for EVERY tab, so it is only safe when every
 * window client of the app is hidden (nobody is mid-sale in any of them).
 * Evaluated inside the worker, the only place that sees all tabs.
 */
export function shouldAutoApplyUpdate(visibilityStates: readonly string[]): boolean {
  return visibilityStates.every((v) => v === 'hidden')
}

// ── What to do when the new worker takes control ──────────────────────────

export type ControllingAction = 'none' | 'reload-now' | 'reload-when-idle' | 'skip' | 'prompt'

/**
 * `acceptedHere`: this tab's user pressed Reload, so reload at once and
 * WITHOUT the loop guard (one click is one reload; guarding it left the
 * button spinning when a fallback reload had used the slot). Other tabs
 * reload when idle, and only if the loop guard allows.
 */
export function controllingAction(input: {
  isUpdate: boolean
  acceptedHere: boolean
  guardOk: boolean
  /** This tab is visible. A visible tab that did not ask for the update
   *  never reloads by itself (it could be mid-sale); it keeps the Reload
   *  prompt instead. */
  visible?: boolean
}): ControllingAction {
  if (!input.isUpdate) return 'none'
  if (input.acceptedHere) return 'reload-now'
  if (input.visible) return 'prompt'
  return input.guardOk ? 'reload-when-idle' : 'skip'
}
